import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore'
import { format } from 'date-fns'
import { db } from '@/lib/firebase'
import { LOCAL_GUEST_SPOTS_COLLECTION, normalizeInstagramHandle } from '@/lib/localGuestSpots'
import type { GuestSpot } from '@/types/models'

export const LOCAL_GUEST_SPOT_MANUAL_OVERRIDES_DOC_ID = 'manualOverrides'

export interface LocalGuestSpotManualOverrideEntry {
  documentId: string
  fingerprint: string
  overriddenFields: string[]
  updatedAt?: Date
  updatedBy?: string | null
}

export function buildLocalGuestSpotFingerprint(
  guestSpot: Pick<GuestSpot, 'username' | 'targetCity' | 'targetCountry' | 'startDate' | 'endDate'>,
): string {
  const normalizedUsername = normalizeInstagramHandle(guestSpot.username) ?? ''
  const normalizedTargetCity = (guestSpot.targetCity ?? '').trim().toLowerCase()
  const normalizedTargetCountry = (guestSpot.targetCountry ?? '').trim().toLowerCase()
  const normalizedStartDate = format(guestSpot.startDate, 'yyyy-MM-dd')
  const normalizedEndDate = format(guestSpot.endDate, 'yyyy-MM-dd')

  return [
    normalizedUsername,
    normalizedTargetCity,
    normalizedTargetCountry,
    normalizedStartDate,
    normalizedEndDate,
  ].join('|')
}

export async function recordLocalGuestSpotManualOverrides(params: {
  guestSpot: GuestSpot
  overriddenFieldNames: string[]
  updatedByUserId?: string
}): Promise<void> {
  if (params.overriddenFieldNames.length === 0) {
    return
  }

  const manualOverridesDocumentRef = doc(
    db,
    LOCAL_GUEST_SPOTS_COLLECTION,
    LOCAL_GUEST_SPOT_MANUAL_OVERRIDES_DOC_ID,
  )
  const manualOverridesSnapshot = await getDoc(manualOverridesDocumentRef)
  const existingManualOverridesData = manualOverridesSnapshot.data() ?? {}
  const existingEntries = (existingManualOverridesData.entries ?? {}) as Record<
    string,
    LocalGuestSpotManualOverrideEntry
  >

  const guestSpotFingerprint = buildLocalGuestSpotFingerprint(params.guestSpot)
  const previousOverrideEntry = existingEntries[guestSpotFingerprint]
  const mergedOverriddenFieldNames = [
    ...new Set([...(previousOverrideEntry?.overriddenFields ?? []), ...params.overriddenFieldNames]),
  ]

  await setDoc(
    manualOverridesDocumentRef,
    {
      entries: {
        ...existingEntries,
        [guestSpotFingerprint]: {
          documentId: params.guestSpot.id,
          fingerprint: guestSpotFingerprint,
          overriddenFields: mergedOverriddenFieldNames,
          updatedAt: serverTimestamp(),
          updatedBy: params.updatedByUserId ?? null,
        },
      },
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}
